import { Grid2 as Grid, Stack } from '@mui/material';
import DropdownIcon from 'components/common/dropdown-icon/DropdownIcon';
import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IDomain, setSelectedDomains, ICustomService } from 'store/conciergeSlice';
import useBundleTranslation from 'i18n';
import DomainBadge from './parts/DomainBadge';
import DomainMenuItem from './parts/DomainMenuItem';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from './styles';
import { useHasTokenString } from 'hooks/useHasTokenString';

export interface IMenuDomain {
    name: string;
    icon_ind: string;
    icon_name: string;
    icon_data: string;
    title: string;
    description?: string;
    suggestedPrompts?: string[];
    selected: boolean;
    menuSelected: boolean;
    header: boolean;
    hasAuth?: boolean;
    needsAuth?: boolean;
    authInfo?: ICustomService['authInfo'];
    serviceName?: string;
}

interface IProps {
    loading: boolean;
    onSelectDomainPrompt: (prompt: string) => void;
    menuOpened: boolean;
    setMenuOpened: (opened: boolean) => void;
}

export default function DomainSelector({ loading, onSelectDomainPrompt, menuOpened, setMenuOpened }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();
    const conciergeSettings = useSelector((state: any) => state.auth?.configuration?.conciergeSettings) ?? {};
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains) as string[];
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];
    const [menuSelectedDomains, setMenuSelectedDomains] = useState<string[]>(selectedDomains);
    const dropdownHandleCloseRef = useRef<null | ((event: any) => void)>(null);
    const skipNextMenuStateChangeRef = useRef<boolean>(false);
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.footerDomainSelector) as typeof defaultStyles;
    }, [customStyles]);

    const createTokenInputIfNeeded = (tokenFieldName: string) => {
        const existingInput = document.querySelector(`[name="${tokenFieldName}"]`);
        if (!existingInput) {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = tokenFieldName;
            input.setAttribute('data-test', tokenFieldName);
            input.value = '';
            document.body.appendChild(input);
        }
    };

    useEffect(() => {
        availableDomains.forEach((domain) => {
            domain.customServices?.forEach((service) => {
                if (service.authInfo?.tokenFieldName) {
                    createTokenInputIfNeeded(service.authInfo.tokenFieldName);
                }
            });
        });
    }, [availableDomains]);

    const domainTokenFields = useMemo(() => {
        const fields: { [domainName: string]: string } = {};
        availableDomains.forEach((domain) => {
            const serviceWithAuth = domain.customServices?.find((service) => service.authInfo?.tokenFieldName);
            if (serviceWithAuth?.authInfo?.tokenFieldName) {
                fields[domain.name] = serviceWithAuth.authInfo.tokenFieldName;
            }
        });
        return fields;
    }, [availableDomains]);

    useEffect(() => {
        if (menuOpened) {
            setMenuSelectedDomains(selectedDomains);
            return;
        }

        if (skipNextMenuStateChangeRef.current) {
            skipNextMenuStateChangeRef.current = false;
            return;
        }

        dispatch(setSelectedDomains(menuSelectedDomains));
        setMenuSelectedDomains([]);
    }, [menuOpened]);

    const menuSelectDomain = (name: string) => {
        let result: string[] = [];

        if (name === '') {
            result = [];
        } else if (menuSelectedDomains.includes(name)) {
            result = menuSelectedDomains.filter((domain) => domain !== name);
        } else {
            result = [...menuSelectedDomains, name];
        }

        setMenuSelectedDomains(result);
        dispatch(setSelectedDomains(result));

        setTimeout(() => {
            skipNextMenuStateChangeRef.current = true;
            dropdownHandleCloseRef.current?.({ target: document.body, __miCloseDropdown: true } as any);
        }, 0);
    };

    const selectDomain = (name: string) => {
        let result: string[] = [];

        if (name === '') {
            dispatch(setSelectedDomains(result));
            return;
        }

        if (selectedDomains.includes(name)) {
            result = selectedDomains.filter((domain) => domain !== name);
        } else {
            result = [...selectedDomains, name];
        }

        setMenuSelectedDomains(result);
        dispatch(setSelectedDomains(result));
    };

    const preparedDomains: IMenuDomain[] = useMemo(() => {
        const domains: IMenuDomain[] = [];

        if (availableDomains.length > 0) {
            domains.push({
                name: '',
                icon_ind: conciergeSettings.bot_icon_ind,
                icon_name: conciergeSettings.bot_icon_name,
                icon_data: conciergeSettings.bot_icon_data,
                title: t('footer.all_sources'),
                selected: selectedDomains.length === 0,
                menuSelected: menuSelectedDomains.length === 0,
                header: true,
            });

            availableDomains.forEach((domain) => {
                const serviceWithAuth = domain.customServices?.find((service) => service.authInfo);
                const hasAuthInfo = !!serviceWithAuth;

                domains.push({
                    name: domain.name,
                    icon_ind: domain.icon_ind,
                    icon_name: domain.icon_name,
                    icon_data: domain.icon_data,
                    title: domain.title,
                    description: domain.description,
                    suggestedPrompts: domain.suggestedPrompts,
                    selected: selectedDomains.includes(domain.name),
                    menuSelected: menuSelectedDomains.includes(domain.name),
                    header: false,
                    hasAuth: hasAuthInfo,
                    authInfo: serviceWithAuth?.authInfo,
                    serviceName: serviceWithAuth?.name,
                });
            });
        }

        return domains;
    }, [availableDomains, selectedDomains, menuSelectedDomains]);

    const selectedDomainBadges = useMemo(() => {
        const badges: IDomain[] = [];

        selectedDomains.forEach((domainName) => {
            const domain = availableDomains.find((t) => t.name === domainName);
            if (domain) {
                badges.push(domain);
            }
        });

        return badges;
    }, [availableDomains, selectedDomains]);

    const selectedDomainsBadgesCount = useMemo(() => {
        return selectedDomainBadges.length;
    }, [selectedDomainBadges]);

    const onSelectPrompt = (domain: string, prompt: string) => {
        onSelectDomainPrompt(prompt);
        dispatch(setSelectedDomains([domain]));
        skipNextMenuStateChangeRef.current = true;
        dropdownHandleCloseRef.current?.({ target: document.body, __miCloseDropdown: true } as any);
    };

    const closeMenu = () => {
        skipNextMenuStateChangeRef.current = true;
        dropdownHandleCloseRef.current?.({ target: document.body, __miCloseDropdown: true } as any);
    };

    return (
        <Grid container direction="row" alignItems="center" justifyContent={'flex-start'} spacing={0.5}>
            <DropdownIcon
                onChangeState={setMenuOpened}
                icon={selectedDomainsBadgesCount > 0 ? 'new' : 'chevron-down'}
                iconFirst={selectedDomainsBadgesCount > 0}
                iconSize={'20px !important'}
                placement={'top-end'}
                handleCloseRef={dropdownHandleCloseRef}
                onClickInside={(event, handleClose) => {
                    if ((event as any)?.__miCloseDropdown) {
                        skipNextMenuStateChangeRef.current = true;
                        handleClose(event as any);
                    }
                }}
                iconButtonProps={styles.iconButton}
                disabled={loading}
                label={selectedDomains.length > 0 ? undefined : t('footer.all_sources')}
                popperSx={styles.dropdownPanel}
            >
                <Stack sx={styles.menuContainer} spacing={0}>
                    {preparedDomains.map((domain, idx) => (
                        <DomainMenuItem
                            key={idx}
                            domain={domain}
                            selectDomain={menuSelectDomain}
                            onSelectPrompt={onSelectPrompt}
                            closeMenu={closeMenu}
                        />
                    ))}
                </Stack>
            </DropdownIcon>
            {selectedDomainBadges.map((domain, idx) => (
                <DomainBadge key={idx} domain={domain} selectDomain={selectDomain} />
            ))}
        </Grid>
    );
}
